// The organisation's Git repository for publishing: the same options as
// the tenant API's Git type sources (SaniPilot "Type sources"), so a
// customer configures a repository the same way in both places - a token
// over HTTPS with the user name preset by the host, or an SSH deploy key
// generated here, plus a CA bundle for a self-hosted server. The builder
// pushes: tokens need write scope, deploy keys write access.
import { useState } from "preact/hooks";
import { Icon } from "./icons";
import type { Actions, PublishingIn, WorkbenchData } from "./model";
import { t } from "../i18n";

interface Provider {
  label: string;
  user: string; // the HTTPS user name with a token
  token: string; // where to create the token, which permission
  ssh?: string; // where to add a deploy key with write access ("" = not possible)
}

// A function, not a constant: the texts follow the current language.
export const providers = (): Record<string, Provider> => ({
  github: { label: "GitHub", user: "x-access-token", token: t("pub.prov.github.token"), ssh: t("pub.prov.github.ssh") },
  bitbucket: { label: t("pub.prov.bitbucket"), user: "x-token-auth", token: t("pub.prov.bitbucket.token"), ssh: "" },
  bitbucket_api: { label: t("pub.prov.bitbucket_api"), user: "x-bitbucket-api-token-auth", token: t("pub.prov.bitbucket_api.token"), ssh: "" },
  gitlab: { label: "GitLab", user: "oauth2", token: t("pub.prov.gitlab.token"), ssh: t("pub.prov.gitlab.ssh") },
  azure: { label: "Azure DevOps", user: "any", token: t("pub.prov.azure.token"), ssh: "" },
  gitea: { label: "Gitea / Forgejo / Codeberg", user: "any", token: t("pub.prov.gitea.token"), ssh: t("pub.prov.gitea.ssh") },
  codecommit: { label: "AWS CodeCommit", user: "", token: t("pub.prov.codecommit.token"), ssh: "" },
  other: { label: t("pub.prov.other"), user: "", token: t("pub.prov.other.token"), ssh: t("pub.prov.other.ssh") },
});

/** Fills {name} placeholders of a translated text with markup. */
function withParts(text: string, parts: Record<string, preact.ComponentChildren>): preact.ComponentChildren[] {
  return text.split(/\{(\w+)\}/).map((x, i) => (i % 2 ? parts[x] ?? `{${x}}` : x));
}

const guess = (url: string): string =>
  /github\.com/.test(url) ? "github" : /bitbucket\.org/.test(url) ? "bitbucket" : /gitlab\.com/.test(url) ? "gitlab"
    : /dev\.azure\.com|visualstudio\.com/.test(url) ? "azure" : /codeberg\.org/.test(url) ? "gitea" : /codecommit/.test(url) ? "codecommit" : "";

export function GitSettings({ data, actions, writer, onSaved }: { data: WorkbenchData; actions: Actions; writer: boolean; onSaved?: () => void }) {
  const p = data.project;
  const [f, setF] = useState({
    url: p.git_url, branch: p.git_branch || "main", provider: p.git_provider || guess(p.git_url) || "github",
    auth: (p.git_auth || "token") as "token" | "ssh", user: p.git_username || "", token: "", ca: "",
  });
  const [replaceToken, setReplaceToken] = useState(!p.has_git_token);
  const [key, setKey] = useState<{ public_key: string; key: string } | null>(null);
  const [showCA, setShowCA] = useState(!!p.git_has_ca);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const PROVIDERS = providers();
  const prov = PROVIDERS[f.provider] ?? PROVIDERS.other;
  const sshPossible = prov.ssh !== "";
  const storedSSH = p.git_auth === "ssh" && !!p.git_public_key;
  const pub = key?.public_key ?? (f.auth === "ssh" && storedSSH ? p.git_public_key : "");

  const save = async (test: boolean) => {
    setBusy(test ? "test" : "save");
    setErr("");
    setOk("");
    const body: PublishingIn = { target: "git", git_url: f.url.trim(), git_branch: f.branch.trim(), git_provider: f.provider, git_auth: f.auth, test };
    if (f.auth === "token") {
      body.git_username = f.user.trim();
      if (replaceToken && f.token.trim()) body.git_token = f.token.trim();
    } else if (key) body.git_key = key.key;
    if (showCA) body.git_ca = f.ca.trim() || (p.git_has_ca ? undefined : "");
    else if (p.git_has_ca) body.git_ca = "";
    try {
      await actions.setPublishing(body);
      setOk(test ? t("pub.git.testedOk") : t("pub.git.saved"));
      setF((x) => ({ ...x, token: "" }));
      setKey(null);
      setReplaceToken(false);
      onSaved?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  };

  const label = (text: string) => <span class="text-[12px] font-semibold text-ink2">{text}</span>;
  return (
    <div class="mt-4 flex flex-col gap-3">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr]">
        <label class="flex flex-col gap-1">
          {label(t("pub.git.hostedOn"))}
          <select class="field" value={f.provider} disabled={!writer} onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            setF({ ...f, provider: v, user: "", auth: PROVIDERS[v]?.ssh === "" ? "token" : f.auth });
          }}>
            {Object.entries(PROVIDERS).map(([k, v]) => <option value={k}>{v.label}</option>)}
          </select>
        </label>
        <fieldset class="m-0 flex flex-col gap-1 border-0 p-0">
          <legend class="mb-1 p-0 text-[12px] font-semibold text-ink2">{t("pub.git.auth")}</legend>
          <div class="flex gap-1 rounded-lg border border-line2 bg-main p-[3px]">
            {(["token", "ssh"] as const).map((a) => (
              <button type="button" disabled={!writer || (a === "ssh" && !sshPossible)} onClick={() => setF({ ...f, auth: a })}
                title={a === "ssh" && !sshPossible ? t("pub.git.noSSH", { provider: prov.label }) : undefined}
                class={`pill h-7 flex-1 justify-center ${f.auth === a ? "bg-sel text-ink" : "text-muted hover:text-ink"} disabled:opacity-50`}>
                {a === "token" ? t("pub.git.authToken") : t("pub.git.authSSH")}
              </button>
            ))}
          </div>
        </fieldset>
        <label class="flex flex-col gap-1">
          {label(f.auth === "ssh" ? t("pub.git.repoSSH") : t("pub.git.repoHTTPS"))}
          <input class="field font-mono" value={f.url} disabled={!writer}
            placeholder={f.auth === "ssh" ? "git@github.com:acme/device-types.git" : "https://github.com/acme/device-types.git"}
            onInput={(e) => {
              const url = (e.target as HTMLInputElement).value;
              const g = guess(url);
              setF({ ...f, url, provider: !p.git_provider && g ? g : f.provider });
            }} />
        </label>
        <label class="flex flex-col gap-1">
          {label(t("pub.git.branch"))}
          <input class="field font-mono" value={f.branch} disabled={!writer} onInput={(e) => setF({ ...f, branch: (e.target as HTMLInputElement).value })} />
        </label>
      </div>

      {f.auth === "token" ? (
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr]">
          <label class="flex flex-col gap-1">
            <span class="text-[12px] font-semibold text-ink2">{t("pub.git.token")} {p.has_git_token && p.git_auth !== "ssh" && <span class="font-normal text-good">{t("pub.git.stored")}</span>}</span>
            {replaceToken || !p.has_git_token || p.git_auth === "ssh" ? (
              <input class="field font-mono" type="password" autocomplete="off" value={f.token} disabled={!writer || !data.me.tokenStorage}
                placeholder={t("pub.git.tokenPlaceholder")} onInput={(e) => setF({ ...f, token: (e.target as HTMLInputElement).value })} />
            ) : (
              <span class="flex items-center gap-2">
                <span class="field flex flex-1 items-center font-mono text-dim">••••••••••••</span>
                {writer && <button type="button" class="btn h-[34px]" onClick={() => setReplaceToken(true)}>{t("pub.git.replaceToken")}</button>}
              </span>
            )}
          </label>
          <label class="flex flex-col gap-1">
            {label(t("pub.git.user"))}
            <input class="field font-mono" value={f.user} disabled={!writer} placeholder={prov.user || t("pub.git.userPlaceholder")}
              onInput={(e) => setF({ ...f, user: (e.target as HTMLInputElement).value })} />
          </label>
          <p class="m-0 text-[12.5px] text-muted sm:col-span-2"><Icon name="info" size={13} stroke={2} class="mr-1 inline align-[-2px]" />{prov.token}
            {" "}{t("pub.git.expire")}</p>
        </div>
      ) : (
        <div class="flex flex-col gap-2 rounded-lg border border-line2 bg-main px-3 py-3">
          {pub ? (
            <>
              <span class="text-[12px] font-semibold text-ink2">{t("pub.git.deployKey")} {key ? <span class="font-normal text-warn">{t("pub.git.keyNew")}</span> : <span class="font-normal text-good">{t("pub.git.stored")}</span>}</span>
              <div class="flex items-start gap-2">
                <code class="block min-w-0 flex-1 break-all rounded bg-side px-2 py-1.5 font-mono text-[12px] text-ink2">{pub}</code>
                <button type="button" class="btn h-7 px-2 text-[12px]" onClick={() => navigator.clipboard?.writeText(pub)}>{t("common.copy")}</button>
              </div>
            </>
          ) : (
            <span class="text-[12.5px] text-muted">{t("pub.git.noKey")}</span>
          )}
          <p class="m-0 text-[12.5px] text-muted"><Icon name="info" size={13} stroke={2} class="mr-1 inline align-[-2px]" />{prov.ssh} {t("pub.git.thenSave")}{" "}
            {p.git_host_key && f.auth === "ssh" && t("pub.git.hostKey", { key: p.git_host_key })}</p>
          {writer && (
            <div>
              <button type="button" class="btn h-7 px-2 text-[12px]" disabled={!!busy || !data.me.tokenStorage}
                onClick={async () => { setErr(""); try { setKey(await actions.newDeployKey()); } catch (e) { setErr((e as Error).message); } }}>
                {pub ? t("pub.git.newKey") : t("pub.git.genKey")}
              </button>
              {pub && !key && <span class="ml-2 text-[12px] text-dim">{t("pub.git.replacesKey")}</span>}
            </div>
          )}
        </div>
      )}

      <div>
        <button type="button" class="text-[12.5px] text-accent2 hover:underline" onClick={() => setShowCA(!showCA)}>
          {showCA ? t("pub.git.hide") : t("pub.git.ca")}{p.git_has_ca && !showCA ? t("pub.git.caStoredShort") : ""}
        </button>
        {showCA && (
          <label class="mt-2 flex flex-col gap-1">
            <span class="text-[12px] font-semibold text-ink2">{t("pub.git.caLabel")} {p.git_has_ca && <span class="font-normal text-good">{t("pub.git.caStored")}</span>}</span>
            <textarea class="field h-auto py-1.5 font-mono text-[12px]" rows={5} spellcheck={false} disabled={!writer} value={f.ca}
              placeholder="-----BEGIN CERTIFICATE-----" onInput={(e) => setF({ ...f, ca: (e.target as HTMLTextAreaElement).value })} />
          </label>
        )}
      </div>

      <p class="m-0 text-[12px] text-muted">{withParts(t("pub.git.footer"), {
        folder: <code class="font-mono">{p.type_id}/</code>,
        tag: <code class="font-mono">{p.type_id}/vX.Y.Z</code>,
        index: <code class="font-mono">index.json</code>,
      })}</p>
      {err && <p class="m-0 text-[13px] text-crit">{err}</p>}
      {ok && <p class="m-0 text-[13px] text-good">{ok}</p>}
      {writer && (
        <div class="flex gap-2">
          <button type="button" class="btn btn-pri" disabled={!!busy || !f.url.trim()} onClick={() => save(true)}>
            {busy === "test" ? t("pub.git.testing") : t("pub.git.testSave")}</button>
          <button type="button" class="btn" disabled={!!busy || !f.url.trim()} title={t("pub.git.saveNoTestTitle")} onClick={() => save(false)}>
            {busy === "save" ? t("pub.saving") : t("pub.git.saveNoTest")}</button>
        </div>
      )}
    </div>
  );
}
